// Table：shadcn 风格表格 + dense variant（T10）。
// dense 时 padding 与字号压缩，用于审计 / 时间线大表。
// 表头一律 text-left（design-token，反 slop check 禁止其他对齐方式）。

import { createContext, forwardRef, useContext, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type Density = "default" | "dense";
const DensityContext = createContext<Density>("default");

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  dense?: boolean;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, dense = false, ...rest }, ref) => (
    <DensityContext.Provider value={dense ? "dense" : "default"}>
      <div className="relative w-full overflow-auto">
        <table
          ref={ref}
          data-density={dense ? "dense" : "default"}
          className={cn(
            "w-full caption-bottom text-sm border-collapse",
            className,
          )}
          {...rest}
        />
      </div>
    </DensityContext.Provider>
  ),
);
Table.displayName = "Table";

export const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...rest }, ref) => (
    <thead
      ref={ref}
      className={cn("border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40", className)}
      {...rest}
    />
  ),
);
TableHeader.displayName = "TableHeader";

export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...rest }, ref) => (
    <tbody ref={ref} className={cn("", className)} {...rest} />
  ),
);
TableBody.displayName = "TableBody";

export const TableFooter = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...rest }, ref) => (
    <tfoot
      ref={ref}
      className={cn("border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 font-medium", className)}
      {...rest}
    />
  ),
);
TableFooter.displayName = "TableFooter";

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...rest }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-[hsl(var(--border))] transition-colors hover:bg-[hsl(var(--muted))]/50 data-[state=selected]:bg-[hsl(var(--muted))]",
        className,
      )}
      {...rest}
    />
  ),
);
TableRow.displayName = "TableRow";

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...rest }, ref) => {
    const density = useContext(DensityContext);
    return (
      <th
        ref={ref}
        className={cn(
          "text-left align-middle font-medium text-[hsl(var(--muted-foreground))]",
          density === "dense" ? "h-8 px-2 text-xs" : "h-10 px-3 text-xs",
          className,
        )}
        {...rest}
      />
    );
  },
);
TableHead.displayName = "TableHead";

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...rest }, ref) => {
    const density = useContext(DensityContext);
    return (
      <td
        ref={ref}
        className={cn(
          "align-middle",
          density === "dense" ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm",
          className,
        )}
        {...rest}
      />
    );
  },
);
TableCell.displayName = "TableCell";

export const TableCaption = forwardRef<HTMLTableCaptionElement, HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...rest }, ref) => (
    <caption
      ref={ref}
      className={cn("mt-2 text-xs text-[hsl(var(--muted-foreground))]", className)}
      {...rest}
    />
  ),
);
TableCaption.displayName = "TableCaption";
